import React, { useState } from 'react';
import { COLORS, TYPOGRAPHY } from '../../../styles/UISystem';

/**
 * FormConnection Component
 * 
 * Form input component for connection information with different states:
 * - placeholder: Shows helper text in light gray
 * - active: Shows cursor, placeholder text disappears
 * - filled: Shows entered text
 * - disabled: Shows text but cannot be interacted with
 */
export const FormConnection = ({ 
  state: initialState = 'placeholder', 
  placeholder = 'Name',
  value = '',
  onChange,
  ...props 
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  
  // Update internal value when prop value changes
  React.useEffect(() => {
    console.log('FormConnection: value prop changed to:', value);
    setInternalValue(value);
  }, [value]);
  
  // Determine current state based on props and internal state
  const state = initialState === 'disabled' ? 'disabled' : 
               (isFocused ? 'active' : 
               (internalValue || value) ? 'filled' : initialState);
               
  // Handle input changes
  const handleChange = (e) => {
    setInternalValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  // Determine background color based on state
  const getBackgroundColor = () => {
    if (state === 'disabled') {
      return COLORS.DISABLED_GRAY;
    }
    return COLORS.LIGHT_GRAY;
  };

  // Determine text style based on state
  const getTextStyle = () => {
    if (state === 'placeholder') {
      return {
        ...TYPOGRAPHY.BODY_LIGHT.style,
        color: COLORS.MEDIUM_GRAY
      };
    }
    return {
      ...TYPOGRAPHY.BODY_REGULAR.style,
      color: COLORS.DARK_GRAY
    };
  };

  return (
    <input
      type="text"
      placeholder={!isFocused ? placeholder : ''}
      value={state === 'disabled' ? value : internalValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={state === 'disabled'}
      style={{
        width: '100%',
        maxWidth: '340px', // Wider than FormProfile (294px)
        height: '40px',
        backgroundColor: getBackgroundColor(),
        border: 'none',
        outline: 'none',
        borderRadius: '8px',
        padding: '0 16px',
        boxSizing: 'border-box',
        ...getTextStyle(),
        ...props.style
      }}
      {...props}
    />
  );
};

/**
 * FormConnectionExample Component
 * 
 * Used for displaying FormConnection examples in the UI System
 */
export const FormConnectionExample = () => {
  const [placeholderValue, setPlaceholderValue] = useState('');
  const [filledValue, setFilledValue] = useState('Mark Mangan');

  return (
    <div style={{ marginBottom: '40px' }}>
      <div style={{ 
        fontSize: '24px', 
        fontWeight: 'bold', 
        color: COLORS.DARK_GRAY, 
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        FormConnection
      </div>
      
      {/* Placeholder state */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ 
          marginBottom: '8px', 
          fontSize: '16px', 
          color: COLORS.DARK_GRAY,
          textAlign: 'center'
        }}>
          placeholder
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FormConnection 
            state="placeholder" 
            placeholder="Name" 
            value={placeholderValue}
            onChange={(e) => setPlaceholderValue(e.target.value)}
          />
        </div>
      </div>
      
      {/* Filled state */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ 
          marginBottom: '8px', 
          fontSize: '16px', 
          color: COLORS.DARK_GRAY,
          textAlign: 'center'
        }}>
          filled
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FormConnection 
            state="filled" 
            value={filledValue}
            onChange={(e) => setFilledValue(e.target.value)}
          />
        </div>
      </div>
      
      {/* Disabled state */}
      <div>
        <div style={{ 
          marginBottom: '8px', 
          fontSize: '16px', 
          color: COLORS.DARK_GRAY,
          textAlign: 'center'
        }}>
          disabled
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FormConnection state="disabled" value="Mark Mangan" />
        </div>
      </div>
    </div>
  );
};

export default FormConnection;
